<template>
  <div class="stax-form">
    <TextField
      v-model="name"
      name="name"
      label="Cardholders Name"
      class="full-width"
      :validation-errors="v$.$errors"
    />
    <div class="d-flex">
      <div class="w-100">
        <div class="label">Card Number</div>
        <div :class="['card-number', 'card-type-' + (cardType || 'none')]">
          <div id="card-number" class="stax-field mb-2"></div>
        </div>
      </div>
      <div class="mx-2" style="width: 300px">
        <TextField
          v-model="expiration"
          name="expiration"
          label="Exp"
          type="tel"
          class="card-expiration w-100"
          placeholder="MM / YYYY"
          :validation-errors="v$.$errors"
          @keypress="exp_typed"
        />
      </div>
      <div style="width: 200px">
        <div class="label">CVV</div>
        <div id="card-cvv" class="stax-field"></div>
      </div>
    </div>
    <BillingAddressForm ref="billingAddressForm" :address="billingAddress" />
    <div id="submit" class="submit-container"></div>
    <div
      v-if="config && config.card_savable && !autoSaveCard"
      class="d-flex flex-column mb-2"
    >
      <label class="mb-0 ml-2" style="font-size: 1.1em">
        <input v-model="saveCard" type="checkbox" />
        Save for future payments.
      </label>
      <span class="ml-2" style="font-size: 0.9em">
        (Saving this payment method replaces any previously saved payment method
        on file.)
      </span>
    </div>
  </div>
</template>

<script>
/* eslint-disable simple-import-sort/imports */
import { handleStaxError } from "@/lib/helpers.js";

import TextField from "@/components/TextField.vue";
import { extractName } from "@/mixins";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import BillingAddressForm from "./BillingAddressForm.vue";
import usePaymentMethods from "../usePaymentMethods";

export default {
  components: {
    TextField,
    BillingAddressForm
  },
  props: {
    autoSaveCard: {
      type: Boolean,
      default: false
    },
    customer: {
      type: Object,
      default: null
    }
  },
  setup() {
    const { billingAddress, paymentMethodsResponse } = usePaymentMethods();
    return {
      paymentMethodsResponse,
      billingAddress,
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      name: { required, $autoDirty: true },
      expiration: { required, $autoDirty: true }
    };
  },
  data() {
    return {
      staxJs: null,
      name: "",
      primaryAddress: {},
      expiration: "",
      saveCard: false,
      cardType: ""
    };
  },
  computed: {
    config() {
      return this.paymentMethodsResponse.find(
        (method) => method.key === "stax_card"
      );
    },
    expirationMonth() {
      if (this.expiration) {
        const split = this.expiration.split("/");
        return split.length ? split[0] : "";
      }
      return "";
    },
    expirationYear() {
      if (this.expiration) {
        const split = this.expiration.split("/");
        let year = split.length > 1 ? split[1] : "";
        if (year) {
          const date = new Date(`1/1/${year}`);
          year = date.getFullYear();
        }
        return year;
      }
      return "";
    },
    firstName() {
      return extractName(this.name).firstName;
    },
    lastName() {
      return extractName(this.name).lastName;
    }
  },
  watch: {
    expiration(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.exp_changed(newValue);
      }
    }
  },
  mounted() {
    this.initForm();
  },
  methods: {
    exp_typed(event) {
      // only allow valid characters (digits or "/") and discard anything longer than 7
      const re = RegExp("[0-9/]+");
      if (!re.test(event.key) || event.target.value.length >= 7) {
        event.preventDefault();
      }
    },
    exp_changed(value) {
      if (!value) return;
      // autocorrect and insert "/" character
      this.expiration = value
        .replace(/[^0-9]/g, "") // remove non-digit character
        .replace(/^([2-9])$/g, "0$1") // convert "2" to "9" to "0X" since there's only 12 months
        .replace(/^(1{1})([3-9]{1})$/g, "0$1/$2") // auto insert the "/" character -> "122" to "12/2" for 3-character string
        .replace(/^0{1,}/g, "0") // replace "0000" with "0"
        .replace(/^([0-1]{1}[0-9]{1})([0-9]{1,4}).*/g, "$1/$2"); // auto insert "/" character for longer string
    },
    async getData() {
      // clear out existing error dialogs for better UX
      window.notify.close();

      const validated = await this.v$.$validate();
      const billingFormData = await this.$refs.billingAddressForm.getData();

      if (!validated) {
        throw new Error();
      }

      const details = {
        method: "card",
        first_name: this.firstName,
        last_name: this.lastName,
        address_1: billingFormData.street || "",
        address_city: billingFormData.city || "",
        address_state: billingFormData.state || "",
        address_zip: billingFormData.zip_code || "",
        month: this.expirationMonth,
        year: this.expirationYear
      };
      try {
        const response = await this.staxJs.tokenize(details);
        if (!response.has_cvv) {
          throw new Error("Please provide a valid CVV");
        }
        return {
          ...billingFormData,
          name: this.name,
          card_token: response.id,
          card_save_payment_method: this.saveCard || this.autoSaveCard
        };
      } catch (err) {
        const error = err; // make debuggable

        handleStaxError(this, error);
      }
    },
    initForm() {
      if (!this.config) {
        return;
      }

      if (this.customer) {
        this.name =
          this.customer.full_name ||
          `${this.customer.first_name} ${this.customer.last_name}`;
        this.primaryAddress =
          this.customer.primary_address ||
          this.customer.addresses.find((address) => address.primary);
      }

      this.staxJs = new StaxJs(this.config.web_id, {
        number: {
          id: "card-number",
          placeholder: "•••• •••• •••• ••••",
          style: "padding: 10px 15px; letter-spacing: 2px; width: 100%",
          type: "text",
          format: "prettyFormat" // the formatting of the CC number (prettyFormat || plainFormat || maskedFormat)
        },
        cvv: {
          id: "card-cvv",
          placeholder: "•••",
          style: "padding: 10px 15px; letter-spacing: 2px",
          type: "text"
        }
      });

      this.staxJs.showCardForm().catch((err) => {
        window.notify.error(err);
        Sentry.captureException(
          err instanceof Error ? err : new Error(String(err))
        );
      });

      // watch for 'cardType' so we can show it visually (with a background image)
      this.staxJs.on("card_form_complete", (message) => {
        this.cardType = message.cardType;
      });

      this.staxJs.on("card_form_incomplete", (message) => {
        this.cardType = message.cardType;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.stax-form {
  :deep(iframe) {
    width: 100%;
    min-height: 39px;
  }

  card-type-none:deep(iframe) {
    background-image: none;
  }

  .card-type-visa:deep(iframe) {
    background: rgba(0, 0, 0, 0) url("@/images/visa.svg") no-repeat bottom 9px
      right 5px;
    background-size: 40px;
  }

  .card-type-master:deep(iframe) {
    background: rgba(0, 0, 0, 0) url("@/images/mastercard.svg") no-repeat bottom
      9px right 5px;
    background-size: 40px;
  }

  .card-type-discover:deep(iframe) {
    background: rgba(0, 0, 0, 0) url("@/images/discover.svg") no-repeat bottom
      9px right 5px;
    background-size: 40px;
  }

  .card-type-american_express:deep(iframe) {
    background: rgba(0, 0, 0, 0) url("@/images/amex.svg") no-repeat bottom 9px
      right 5px;
    background-size: 40px;
  }

  .card-expiration {
  }
  .card-expiration :deep(input) {
    letter-spacing: 2px;
  }

  .label {
    margin-bottom: 3px;
  }

  .submit-container {
    display: none;
  }

  .stax-field {
    height: 35px;
    background-color: #fff;
    border-radius: 0.25rem;
    border: 1px solid #ced4da;
    box-shadow: none;
  }
}
</style>
